(function () {
  // Prevent this script from being loaded more than once.
  if (window.__tnc_enforcer_loaded__) return;
  window.__tnc_enforcer_loaded__ = true;

  // Pull configuration from the popup snippet.
  const CONFIG = window.tncConfig || {};
  const TNC_VERSION = CONFIG.version || "1.0";
  const SCRIPT_URL = CONFIG.scriptUrl || "";
  const TERMS_URL = CONFIG.termsUrl || "/policies/terms-of-service";

  // Academy notice: an extra required acknowledgement when the cart holds an Academy product.
  const ACADEMY = window.grAcademyNotice || {};
  const ACADEMY_VERSION = ACADEMY.version || "1.0";
  const ACADEMY_LOG_URL = ACADEMY.logUrl || "";
  const ACADEMY_TYPES = ACADEMY.productTypes || [];
  const ACADEMY_HANDLES = ACADEMY.handles || [];
  const REFUND_TEXT = "Digital files are non-refundable due to the digital nature of the order. Please read our full ";
  const ACADEMY_LABEL = "I understand Gather 'Round Academy is a homeschool support service, not an accredited institution, and I agree to the Terms &amp; Conditions.";
  const ACADEMY_TEXT =
    "Gather 'Round Academy is a homeschool support service, not a school. It is not accredited and does " +
    "not act as a school of record, umbrella school, or cover school. Enrolling does not register your " +
    "child with any school. Parents remain the home educator and are responsible for meeting their " +
    "state's or province's homeschool and attendance laws, including any notices, records, or testing " +
    "required. Report cards and transcripts are parent-support records.";

  // Whether the popup currently open needs the Academy acknowledgement too.
  let academyRequired = false;

  // Last known cart state, so accelerated (Shop Pay) buttons can be decided on synchronously.
  // null until the cart has been read once.
  let cartState = null;
  let lastCart = null;

  // Checkout button selectors.
  // This covers cart page buttons, drawer checkout buttons, and common custom checkout triggers.
  const SELECTORS = {
    checkoutButtons: [
      'a[href="/checkout"]',
      'a[href^="/checkout"]',
      'a[href*="/checkout?"]',
      'a[href$="/checkout"]',
      'button[name="checkout"]',
      'input[name="checkout"]',
      'button[type="submit"][formaction*="/checkout"]',
      'input[type="submit"][formaction*="/checkout"]',
      '[data-checkout-button]',
      '.cart__checkout-button',
      '.cart__submit',
      '.drawer__checkout',
      '.cart-drawer__checkout'
    ].join(",")
  };

  // Small helper to get a single element.
  function $(selector, root = document) {
    return root.querySelector(selector);
  }

  // Small helper to get multiple elements as an array.
  function $all(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
  }

  // Get all popup-related elements in one place.
  function getPopupEls() {
    return {
      overlay: $("#termsOverlay"),
      popup: $("#termsPopup"),
      checkbox: $("#agreeCheckbox"),
      continueButton: $("#continueButton"),
      emailWrapper: $("#emailFieldWrapper"),
      emailInput: $("#emailInput"),
      termsLink: $("#tncTermsLink"),
      academyNotice: $("#academyNotice"),
      academyCheckbox: $("#academyAgreeCheckbox")
    };
  }

  // Ensure popup markup exists, even if snippet failed to render on a template.
  function ensurePopupExists() {
    let { overlay, popup } = getPopupEls();
    if (overlay && popup) return;

    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "termsOverlay";
      overlay.className = "terms-overlay";
      overlay.hidden = true;
      document.body.appendChild(overlay);
    }

    if (!popup) {
      popup = document.createElement("div");
      popup.id = "termsPopup";
      popup.className = "terms-popup";
      popup.setAttribute("role", "dialog");
      popup.setAttribute("aria-modal", "true");
      popup.setAttribute("aria-labelledby", "tnc-title");
      popup.hidden = true;
      popup.innerHTML = `
        <div class="terms-popup-content">
          <h2 id="tnc-title">Before you continue</h2>
          <p id="tncIntro">
            Digital files are non-refundable due to the digital nature of the order.
            Please read our full
            <a id="tncTermsLink" href="${TERMS_URL}" target="_blank" rel="noopener">
              Terms &amp; Conditions
            </a>.
          </p>
          <div id="academyNotice" class="academy-notice" hidden>
            <p>${REFUND_TEXT}<a href="${TERMS_URL}" target="_blank" rel="noopener">Terms &amp; Conditions</a>.</p>
            <p class="academy-text"></p>
            <label class="terms-checkbox">
              <input type="checkbox" id="academyAgreeCheckbox">
              <span>${ACADEMY_LABEL}</span>
            </label>
          </div>
          <label class="terms-checkbox" id="tncAgreeRow">
            <input type="checkbox" id="agreeCheckbox">
            <span>I agree to the Terms &amp; Conditions</span>
          </label>
          <div class="terms-actions">
            <button type="button" id="continueButton" disabled>Continue to Checkout</button>
          </div>
        </div>
      `;
      popup.querySelector("#academyNotice .academy-text").textContent = ACADEMY_TEXT;
      document.body.appendChild(popup);
    }
  }

  // Basic email validation for guest checkouts.
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Fetch the current Shopify cart as JSON.
  async function getCart() {
    const response = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });

    if (!response.ok) {
      throw new Error("Could not load cart");
    }

    return response.json();
  }

  // Whether the cart holds an Academy product (by product type or handle).
  function cartNeedsAcademy(cart) {
    return !!(
      cart &&
      Array.isArray(cart.items) &&
      cart.items.some(
        (item) => ACADEMY_TYPES.includes(item.product_type) || ACADEMY_HANDLES.includes(item.handle)
      )
    );
  }

  // Check whether the cart already has acceptance saved for the current T&C version,
  // and, when it holds an Academy product, for the current Academy notice version.
  // If it does, the customer can continue straight to checkout without seeing the popup again.
  function cartHasCurrentAcceptance(cart) {
    const attributes = (cart && cart.attributes) || {};
    const termsAccepted =
      attributes["T&C Accepted"] === "Yes" && attributes["T&C Version"] === TNC_VERSION;
    const academyAccepted =
      !cartNeedsAcademy(cart) ||
      (attributes["Academy Not-a-School Notice Accepted"] === "Yes" &&
        attributes["Academy Not-a-School Notice Version"] === ACADEMY_VERSION);
    return termsAccepted && academyAccepted;
  }

  // Read the cart and remember what it needs.
  async function refreshCartState() {
    const cart = await getCart();
    lastCart = cart;
    cartState = { academy: cartNeedsAcademy(cart), accepted: cartHasCurrentAcceptance(cart) };
    return cart;
  }

  // Save the acceptance details to Shopify cart attributes.
  // These attributes usually flow through to the final order.
  async function saveAcceptanceToCart(email, ip) {
    const timestamp = new Date().toISOString();
    const academyAttributes = academyRequired
      ? {
          "Academy Not-a-School Notice Accepted": "Yes",
          "Academy Not-a-School Notice Version": ACADEMY_VERSION,
          "Academy Not-a-School Notice Timestamp": timestamp,
          // Lets the order find its row in the agreement log (Shopify Flow sends it back with the order).
          "Academy Not-a-School Notice Cart": (lastCart && lastCart.token) || ""
        }
      : {};
    const response = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        attributes: {
          "T&C Accepted": "Yes",
          "T&C Version": TNC_VERSION,
          "T&C Email": email,
          "T&C IP": ip,
          "T&C Timestamp": timestamp,
          ...academyAttributes
        }
      })
    });

    if (!response.ok) {
      throw new Error("Could not save T&C data to cart");
    }

    return response.json();
  }

  // Look up the visitor's public IP address.
  // If it fails, continue with "unknown" so the flow does not break.
  async function getIP() {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (!response.ok) {
        throw new Error("IP lookup failed");
      }

      const data = await response.json();
      return data.ip || "unknown";
    } catch (error) {
      return "unknown";
    }
  }

  // Send the same acceptance data to Google Sheets through your Apps Script endpoint.
  // no-cors means the browser will not give full success/failure feedback, but the request will still be sent.
  async function sendToGoogleSheet(payload) {
    if (!SCRIPT_URL) return;

    try {
      await fetch(SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.warn("[TNC] Google Sheet logging failed", error);
    }
  }

  // Record an Academy agreement in the Academy tab of the T&C Acceptance Log.
  // The order keeps the same record in its cart attributes; this is the searchable list.
  async function sendToAcademyLog(payload) {
    if (!ACADEMY_LOG_URL) return;

    try {
      await fetch(ACADEMY_LOG_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.warn("[TNC] Academy log failed", error);
    }
  }

  // Open the popup and show the guest email field if the customer is not logged in.
  function openPopup() {
    ensurePopupExists();
    const { overlay, popup, emailWrapper, emailInput, termsLink, academyNotice, academyCheckbox } = getPopupEls();
    if (!overlay || !popup) {
      // Never dead-end checkout: if popup shell cannot be shown, continue.
      redirectToCheckout();
      return;
    }

    // Make sure the Terms link matches the configured URL.
    if (termsLink) {
      termsLink.setAttribute("href", TERMS_URL);
    }

    // Show the Academy notice only for Academy carts; it carries the digital-files line itself.
    if (academyNotice) {
      academyNotice.hidden = !academyRequired;
    }
    const intro = $("#tncIntro");
    if (intro) {
      intro.hidden = academyRequired;
    }
    // One checkbox for Academy carts: the Academy one covers the Terms too.
    const termsRow = $("#tncAgreeRow");
    if (termsRow) {
      termsRow.hidden = academyRequired;
    }
    if (academyCheckbox && !academyRequired) {
      academyCheckbox.checked = false;
    }
    syncContinueState();

    overlay.hidden = false;
    popup.hidden = false;
    document.body.classList.add("tnc-popup-open");

    // Only guests need to enter an email.
    if (!window.customerEmail && emailWrapper) {
      emailWrapper.hidden = false;

      if (emailInput) {
        setTimeout(() => emailInput.focus(), 0);
      }
    }
  }

  // Close the popup and restore page scrolling.
  function closePopup() {
    const { overlay, popup } = getPopupEls();
    if (!overlay || !popup) return;

    overlay.hidden = true;
    popup.hidden = true;
    document.body.classList.remove("tnc-popup-open");
  }

  // Debug helpers so popup can be forced open/closed from browser console.
  function registerDebugHelpers() {
    window.TNC_DEBUG = {
      open() {
        openPopup();
        bindPopupEvents();
        syncContinueState();
      },
      close() {
        closePopup();
      },
      showGuestEmail() {
        const { emailWrapper } = getPopupEls();
        if (emailWrapper) emailWrapper.hidden = false;
      },
      enableButton() {
        const { continueButton } = getPopupEls();
        if (!continueButton) return;
        continueButton.disabled = false;
        continueButton.classList.add("enabled");
      }
    };
  }

  // Enable or disable the Continue button based on whether the checkbox is checked.
  function syncContinueState() {
    const { checkbox, continueButton, academyCheckbox } = getPopupEls();
    if (!checkbox || !continueButton) return;

    const ready = academyRequired ? !!(academyCheckbox && academyCheckbox.checked) : checkbox.checked;
    continueButton.disabled = !ready;
    continueButton.classList.toggle("enabled", ready);
  }

  // Send the customer to Shopify checkout.
  function redirectToCheckout() {
    window.location.href = "/checkout";
  }

  // Handle the popup Continue button.
  // Validate the checkbox and guest email, save data to cart and Google Sheets, then continue to checkout.
  async function handleContinue() {
    const { checkbox, continueButton, emailInput, emailWrapper, academyCheckbox } = getPopupEls();
    if (!checkbox || !continueButton) return;
    const agreed = academyRequired ? !!(academyCheckbox && academyCheckbox.checked) : checkbox.checked;
    if (!agreed) return;

    const needsEmail = emailWrapper && !emailWrapper.hidden;
    const email = (window.customerEmail || (emailInput ? emailInput.value.trim() : "") || "").trim();

    if (needsEmail && !email) {
      alert("Please enter your email.");
      return;
    }

    if (needsEmail && !isValidEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    continueButton.disabled = true;
    continueButton.textContent = "Processing...";

    try {
      const ip = await getIP();

      await saveAcceptanceToCart(email, ip);

      await sendToGoogleSheet({
        email,
        ip,
        accepted: "Yes",
        version: TNC_VERSION,
        academyNotice: academyRequired ? "Yes" : "",
        academyNoticeVersion: academyRequired ? ACADEMY_VERSION : "",
        timestamp: new Date().toISOString(),
        page: window.location.href
      });

      if (academyRequired) {
        await sendToAcademyLog({
          timestamp: new Date().toISOString(),
          email,
          ip,
          version: ACADEMY_VERSION,
          accepted: "Yes",
          store: window.location.hostname,
          cartToken: (lastCart && lastCart.token) || "",
          items: ((lastCart && lastCart.items) || [])
            .filter((item) => ACADEMY_TYPES.includes(item.product_type) || ACADEMY_HANDLES.includes(item.handle))
            .map((item) => item.product_title || item.handle)
            .join(", "),
          page: window.location.href
        });
      }

      closePopup();
      redirectToCheckout();
    } catch (error) {
      console.error("[TNC] Save failed", error);
      continueButton.disabled = false;
      continueButton.textContent = "Continue to Checkout";
      syncContinueState();
      alert("Could not save your acceptance. Please try again.");
    }
  }

  // Intercept all checkout attempts.
  // If the cart already has valid acceptance saved, continue normally.
  // Otherwise show the popup first.
  async function interceptCheckout(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }

    try {
      const cart = await refreshCartState();
      academyRequired = cartState.academy;

      if (cartHasCurrentAcceptance(cart)) {
        redirectToCheckout();
        return;
      }

      openPopup();
    } catch (error) {
      openPopup();
    }
  }

  // Shop Pay and other accelerated checkout buttons in the cart skip the Checkout button.
  // When the cart holds an Academy product that hasn't been acknowledged, stop them and show the
  // popup; Continue then goes to the normal checkout, where Shop Pay is still offered.
  const ACCELERATED = "shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .additional-checkout-buttons, [data-shopify=\"dynamic-checkout-cart\"]";

  function interceptAccelerated(event) {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(ACCELERATED)) return;
    if (cartState && (!cartState.academy || cartState.accepted)) return;
    interceptCheckout(event);
  }

  // Keep the cart state fresh when a cart area re-renders (drawer, cart page, notification).
  let refreshTimer = null;
  function scheduleCartRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refreshCartState().catch(() => {}), 400);
  }

  function touchesCart(mutations) {
    return mutations.some((mutation) => {
      const node = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      return !!(node && node.closest('[id*="cart" i], [class*="cart" i]'));
    });
  }

  // Some themes submit checkout via form submit handlers rather than direct button clicks.
  // This catches those cases consistently.
  async function interceptCheckoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter || null;
    const submitterLooksLikeCheckout =
      !!submitter &&
      submitter.matches(
        [
          'button[name="checkout"]',
          'input[name="checkout"]',
          '[data-checkout-button]',
          '.cart__checkout-button',
          '.cart__submit',
          '.drawer__checkout',
          '.cart-drawer__checkout',
          'button[type="submit"][formaction*="/checkout"]',
          'input[type="submit"][formaction*="/checkout"]'
        ].join(",")
      );

    const formLooksLikeCheckout =
      form.matches('form[action*="/checkout"]') ||
      !!$('[name="checkout"], [data-checkout-button], .cart__checkout-button, .cart__submit, .drawer__checkout, .cart-drawer__checkout', form);

    if (!submitterLooksLikeCheckout && !formLooksLikeCheckout) return;

    await interceptCheckout(event);
  }

  // Find all checkout buttons currently on the page and attach the interception logic.
  // This matters for themes that re-render cart drawers dynamically.
  function bindCheckoutButtons() {
    $all(SELECTORS.checkoutButtons).forEach((button) => {
      if (button.__tnc_bound__) return;

      button.__tnc_bound__ = true;
      button.addEventListener("click", interceptCheckout, true);
    });
  }

  // Bind events for popup close, checkbox changes, and the Continue button.
  function bindPopupEvents() {
    const { overlay, checkbox, continueButton } = getPopupEls();

    if (overlay && !overlay.__tnc_bound__) {
      overlay.__tnc_bound__ = true;
      overlay.addEventListener("click", closePopup);
    }

    if (checkbox && !checkbox.__tnc_bound__) {
      checkbox.__tnc_bound__ = true;
      checkbox.addEventListener("change", syncContinueState);
    }

    const { academyCheckbox } = getPopupEls();
    if (academyCheckbox && !academyCheckbox.__tnc_bound__) {
      academyCheckbox.__tnc_bound__ = true;
      academyCheckbox.addEventListener("change", syncContinueState);
    }

    if (continueButton && !continueButton.__tnc_bound__) {
      continueButton.__tnc_bound__ = true;
      continueButton.addEventListener("click", handleContinue);
    }
  }

  // Start everything once the page has loaded.
  // A MutationObserver is used so checkout buttons added later by AJAX or cart drawers are also handled.
  function init() {
    ensurePopupExists();
    bindPopupEvents();
    bindCheckoutButtons();
    syncContinueState();
    registerDebugHelpers();

    // Capture submit-based checkout paths globally.
    document.addEventListener("submit", interceptCheckoutSubmit, true);
    document.addEventListener("click", interceptAccelerated, true);
    refreshCartState().catch(() => {});

    const observer = new MutationObserver((mutations) => {
      bindCheckoutButtons();
      bindPopupEvents();
      if (touchesCart(mutations)) scheduleCartRefresh();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();